import { useState } from 'react';

import { Badge, Button, Dialog, Empty, Skeleton } from '../../../components/ui';
import { useLoi } from '../../../hooks/useLoi';
import { useT } from '../../../i18n';
import { ngayGioVN } from '../../../lib/dinh-dang';
import { LoiTrongHop } from '../../tenant/components/phan-form';
import { TRUONG_KHOA } from '../constants';
import { useKhoiPhucHoSo, useLichSuHoSo } from '../hooks/useHoSoToChuc';
import { moTaThayDoi } from '../nghiep-vu/ho-so';
import type { TenantProfileChange, TenantProfileVersion } from '../types';

/**
 * Lịch sử thay đổi nhận diện và khôi phục (ADR-UI-0016 mục 6).
 *
 * Khôi phục **không xoá** lịch sử: nó tạo một phiên bản mới mang nội dung của
 * phiên bản được chọn. Vì vậy hộp xác nhận nói rõ việc này có thể hoàn tác.
 */
export function HopLichSu({
  open,
  phienBanHienTai,
  onClose,
}: {
  open: boolean;
  /** Phiên bản đang dùng, để đánh dấu và không cho khôi phục chính nó. */
  phienBanHienTai: number;
  onClose: () => void;
}) {
  const { t, locale } = useT();
  const dichLoi = useLoi();
  const lichSu = useLichSuHoSo(open);
  const khoiPhuc = useKhoiPhucHoSo();
  const [chon, setChon] = useState<TenantProfileVersion | null>(null);

  const dongXacNhan = () => {
    setChon(null);
    khoiPhuc.reset();
  };

  const moTa = (c: TenantProfileChange) => {
    const m = moTaThayDoi(c);
    const truong = t(TRUONG_KHOA[m.truong]);
    if (m.kieu === 'chu') {
      return t('hoSoToChuc.lichSu.chu', {
        truong,
        truoc: m.truoc ?? t('hoSoToChuc.lichSu.trong'),
        sau: m.sau ?? t('hoSoToChuc.lichSu.trong'),
      });
    }
    if (m.kieu === 'anhMoi') return t('hoSoToChuc.lichSu.anhMoi', { truong });
    if (m.kieu === 'anhDoi') return t('hoSoToChuc.lichSu.anhDoi', { truong });
    return t('hoSoToChuc.lichSu.anhGo', { truong });
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        title={t('hoSoToChuc.lichSu.tieuDe')}
        width={520}
        destroyOnHidden
        footer={
          <div className="flex justify-end">
            <Button variant="outline" size="md" onClick={onClose}>
              {t('chung.dong')}
            </Button>
          </div>
        }
      >
        <div className="pt-2">
          {lichSu.isPending ? (
            <div className="space-y-3" aria-busy>
              {[0, 1, 2].map((i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : lichSu.isError ? (
            <Empty
              title={t('hoSoToChuc.lichSu.loiTai')}
              description={dichLoi(lichSu.error)}
              action={
                <Button variant="secondary" size="sm" onClick={() => void lichSu.refetch()}>
                  {t('chung.thuLai')}
                </Button>
              }
            />
          ) : lichSu.data.items.length === 0 ? (
            <Empty title={t('hoSoToChuc.lichSu.rong')} />
          ) : (
            <ol className="space-y-3">
              {lichSu.data.items.map((v) => {
                const hienHanh = v.version === phienBanHienTai;
                return (
                  <li key={v.version} className="rounded-lg border border-default p-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="flex flex-wrap items-center gap-2 text-sm font-medium text-content-primary">
                          {t('hoSoToChuc.lichSu.phienBan', { so: v.version })}
                          {hienHanh && <Badge variant="primary">{t('hoSoToChuc.lichSu.hienHanh')}</Badge>}
                        </p>
                        <p className="text-2xs text-content-muted">
                          {ngayGioVN(v.created_at, locale)} · {t('hoSoToChuc.lichSu.boi', { nguoi: v.created_by })}
                        </p>
                      </div>
                      {!hienHanh && (
                        <Button variant="secondary" size="sm" onClick={() => setChon(v)}>
                          {t('hoSoToChuc.lichSu.khoiPhuc')}
                        </Button>
                      )}
                    </div>
                    {v.restored_from !== null && (
                      <p className="mt-1 text-2xs text-content-muted">
                        {t('hoSoToChuc.lichSu.khoiPhucTu', { so: v.restored_from })}
                      </p>
                    )}
                    <ul className="mt-2 space-y-0.5 text-xs text-content-secondary">
                      {v.changes.map((c) => (
                        <li key={c.field} className="min-w-0 break-words">
                          {moTa(c)}
                        </li>
                      ))}
                    </ul>
                  </li>
                );
              })}
            </ol>
          )}
        </div>
      </Dialog>

      <Dialog
        open={chon !== null}
        onClose={dongXacNhan}
        title={t('hoSoToChuc.lichSu.xnTieuDe', { so: chon?.version ?? 0 })}
        width={440}
        destroyOnHidden
        footer={
          <div className="flex flex-wrap justify-end gap-3">
            <Button variant="outline" size="md" onClick={dongXacNhan}>
              {t('chung.huy')}
            </Button>
            <Button
              variant="primary"
              size="md"
              loading={khoiPhuc.isPending}
              onClick={() =>
                chon &&
                khoiPhuc.mutate({ v: chon.version, phienBan: phienBanHienTai }, { onSuccess: dongXacNhan })
              }
            >
              {khoiPhuc.isPending
                ? t('hoSoToChuc.lichSu.dangKhoiPhuc')
                : t('hoSoToChuc.lichSu.nutKhoiPhuc', { so: chon?.version ?? 0 })}
            </Button>
          </div>
        }
      >
        <div className="space-y-3 pt-2">
          <p className="text-sm leading-relaxed text-content-secondary">
            {t('hoSoToChuc.lichSu.xnThan', { so: chon?.version ?? 0 })}
          </p>
          {khoiPhuc.isError && <LoiTrongHop>{dichLoi(khoiPhuc.error)}</LoiTrongHop>}
        </div>
      </Dialog>
    </>
  );
}
